import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  GraduationCap,
  Award,
  Briefcase,
  DollarSign,
  CheckCircle2,
  TrendingUp,
  User,
  Shield,
  Target,
  RefreshCw,
  Calendar,
  PieChart,
  Activity,
} from "lucide-react";
import { AnalystProfileSettings } from "./AnalystProfileSettings";
import { activityLogService } from "../../services/activityLogService";
import { CERTIFICATIONS, RESEARCH_STYLES, RISK_TOLERANCE_LEVELS } from "../../utils/financialTaxonomy";
import type { FeeStructureOption } from "../../interface/profile/FeeStructureOption";

const getCertLabel = (cert: string) => {
  const trimmed = cert.trim();
  const found = CERTIFICATIONS.find(c => c.value.toUpperCase() === trimmed.toUpperCase());
  return found ? found.label : trimmed;
};

const formatFeeValue = (fee: FeeStructureOption) => {
  const parts: string[] = [];
  if (fee.aumPercentage != null) parts.push(fee.aumPercentage > 100 ? `$${fee.aumPercentage}` : `${fee.aumPercentage}%`);
  if (fee.hourlyRate != null) parts.push(`$${fee.hourlyRate}/hr`);
  if (fee.flatFeeAmount != null) parts.push(`$${fee.flatFeeAmount}`);
  if (fee.performanceFeePercentage != null) parts.push(`${fee.performanceFeePercentage}% perf`);
  if (fee.minimumFee != null) parts.push(`min $${fee.minimumFee}`);
  return parts.join(" | ");
};

interface AnalystOverviewProps {
  certifications?: string[];
  focusSectors?: string[];
  investmentFocus?: string[];
  researchExpertise?: string[];
  researchApproach?: string;
  yearsExperience?: string;
  feeStructureOptions?: FeeStructureOption[];
  clientRiskTolerance?: string[];
  avgClientReturn?: number;
  totalClients?: number;
  consultationAvailable?: boolean;
  consultationDuration?: number;
  acceptingNewClients?: boolean;
}

export function AnalystOverview({ certifications, focusSectors, investmentFocus, researchExpertise, researchApproach, yearsExperience, feeStructureOptions, clientRiskTolerance, avgClientReturn, totalClients, consultationAvailable, consultationDuration, acceptingNewClients }: AnalystOverviewProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Credentials & Expertise
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {certifications && certifications.length > 0 ? certifications.map((cert, i) => (
                <div key={i} className="flex items-start gap-3 p-3 border rounded-lg">
                  <Award className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm">{getCertLabel(cert)}</p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground">No certifications listed</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Practice Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center p-3 border rounded-lg">
              <span className="text-sm font-semibold">Total Clients</span>
              <span>{totalClients != null ? totalClients : "N/A"}</span>
            </div>
            <div className="flex justify-between items-center p-3 border rounded-lg">
              <span className="text-sm font-semibold">Years of Experience</span>
              <span>{yearsExperience || "N/A"}</span>
            </div>
            <div className="flex justify-between items-center p-3 border rounded-lg">
              <span className="text-sm font-semibold">Avg Client Return</span>
              <span className="text-green-600">{avgClientReturn != null ? `+${avgClientReturn.toFixed(1)}%` : "N/A"}</span>
            </div>
          </CardContent>
          {focusSectors && focusSectors.length > 0 && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <span className="text-sm font-semibold">Focus Sectors</span>
                <div className="flex flex-wrap gap-2">
                  {focusSectors.map((s, i) => <Badge key={i} variant="secondary">{s}</Badge>)}
                </div>
              </div>
            </CardContent>
          )}
          {investmentFocus && investmentFocus.length > 0 && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <span className="text-sm font-semibold">Investment Focus</span>
                <div className="flex flex-wrap gap-2">
                  {investmentFocus.map((f, i) => <Badge key={i} variant="outline">{f}</Badge>)}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {researchExpertise && researchExpertise.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Research Expertise</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {researchExpertise.map((r, i) => {
                  const style = RESEARCH_STYLES.find(s => s.value === r);
                  return <Badge key={i} variant="secondary">{style?.label || r}</Badge>;
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {researchApproach && (
          <Card>
            <CardHeader>
              <CardTitle>Research Approach</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{researchApproach}</p>
            </CardContent>
          </Card>
        )}
        {clientRiskTolerance && clientRiskTolerance.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Preferred Client Risk Tolerance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {clientRiskTolerance.map((rt, i) => {
                  const level = RISK_TOLERANCE_LEVELS.find(l => l.value === rt);
                  return <Badge key={i} variant="outline">{level?.label || rt}</Badge>;
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fee Structure & Service Offerings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-blue-600" />
            Fee Structure & Service Offerings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {feeStructureOptions && feeStructureOptions.length > 0 ? (
            <div className="space-y-3">
              {feeStructureOptions.map((fee) => (
                <div key={fee.id} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{fee.name}</span>
                    <Badge variant="secondary" className="text-xs">{fee.feeType.replace(/_/g, " ")}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">{formatFeeValue(fee)}</div>
                  {fee.billingFrequency && <p className="text-xs text-muted-foreground mt-1">Billing: {fee.billingFrequency}</p>}
                  {fee.description && <p className="text-xs italic mt-1">{fee.description}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No fee structures configured</p>
          )}
        </CardContent>
      </Card>

      {/* Consultation & Availability */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Consultation & Availability
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg space-y-1">
              <p className="text-sm font-semibold">Free Initial Consultation</p>
              {consultationAvailable ? (
                <>
                  <Badge variant="secondary">Available</Badge>
                  {consultationDuration != null && <p className="text-sm text-muted-foreground mt-1">{consultationDuration} minute consultation</p>}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Not offered</p>
              )}
            </div>
            <div className="p-4 border rounded-lg space-y-1">
              <p className="text-sm font-semibold">New Client Availability</p>
              {acceptingNewClients ? (
                <p className="text-sm text-green-600 font-medium">Accepting New Clients</p>
              ) : (
                <p className="text-sm text-orange-600 font-medium">Not Accepting</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AnalystActivity({ userId }: { userId?: string }) {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    activityLogService.getActivityLogsByUser(userId)
      .then(res => {
        const data = res?.data || res || [];
        setActivities(Array.isArray(data) ? data : []);
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [userId]);

  const getActivityIcon = (type?: string) => {
    const t = type?.toLowerCase() || "";
    if (t.includes("rebalanc") || t.includes("trade") || t.includes("buy") || t.includes("sell"))
      return { icon: RefreshCw, bg: "bg-green-100 dark:bg-green-900", color: "text-green-600 dark:text-green-300" };
    if (t.includes("meeting") || t.includes("call") || t.includes("appointment"))
      return { icon: Calendar, bg: "bg-blue-100 dark:bg-blue-900", color: "text-blue-600 dark:text-blue-300" };
    if (t.includes("goal") || t.includes("target"))
      return { icon: Target, bg: "bg-purple-100 dark:bg-purple-900", color: "text-purple-600 dark:text-purple-300" };
    if (t.includes("deposit") || t.includes("withdrawal") || t.includes("transfer") || t.includes("payment"))
      return { icon: DollarSign, bg: "bg-orange-100 dark:bg-orange-900", color: "text-orange-600 dark:text-orange-300" };
    if (t.includes("alloc") || t.includes("portfolio"))
      return { icon: PieChart, bg: "bg-indigo-100 dark:bg-indigo-900", color: "text-indigo-600 dark:text-indigo-300" };
    return { icon: Activity, bg: "bg-gray-100 dark:bg-gray-800", color: "text-gray-600 dark:text-gray-300" };
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading activities...</p>
        ) : activities.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent activity</p>
        ) : (
          <div className="space-y-4">
            {activities.map((a: any, i: number) => {
              const { icon: Icon, bg, color } = getActivityIcon(a.activityType);
              return (
                <div key={a.id || i} className="flex items-start gap-3 pb-3 border-b last:border-b-0">
                  <div className={`p-2 rounded-full ${bg}`}>
                    <Icon className={`h-4 w-4 ${color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{a.description || a.activityType}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(a.createdAt || a.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface AnalystSettingsProps {
  feeStructureOptions?: FeeStructureOption[];
  consultationAvailable?: boolean;
  consultationDuration?: number;
  acceptingNewClients?: boolean;
}

export function AnalystSettings({ feeStructureOptions, consultationAvailable, consultationDuration, acceptingNewClients }: AnalystSettingsProps) {
  return (
    <div>
      <AnalystProfileSettings feeStructureOptions={feeStructureOptions} consultationAvailable={consultationAvailable} consultationDuration={consultationDuration} acceptingNewClients={acceptingNewClients} />
    </div>
  );
}
